// ============================================================
// src/domain/vision.ts  —  AI INSPEKTOR BUDOWLANY
// JEDYNE miejsce gdzie wywołujemy Gemini Vision.
// Zero UI. Zero side-effectów poza Promise.
// ============================================================

import { SpatialData } from './types';

const VISION_PROMPT = `
Jesteś Zaawansowanym Inspektorem Budowlanym AI. Analizujesz zdjęcie pomieszczenia budowlanego.

KROK 1 — ZNAJDŹ PUNKT ODNIESIENIA (SKALA):
Zidentyfikuj w kadrze jeden z tych obiektów (w kolejności priorytetu):
- Standardowe drzwi wewnętrzne: szerokość 80–90 cm, wysokość 200–210 cm
- Gniazdko elektryczne / włącznik: szerokość 80 mm, wysokość 80 mm
- Standardowa cegła: 25 cm × 6,5 cm
- Okno standardowe: szerokość 90–120 cm, wysokość 120–150 cm

Zapisz: który obiekt wybrałeś i jakie wymiary przyjąłeś jako skalę (w cm).

KROK 2 — OBLICZ WYMIARY POMIESZCZENIA:
Używając wybranego punktu odniesienia jako skali px/cm:
- Oszacuj szerokość każdej widocznej ściany (mb)
- Oszacuj wysokość pomieszczenia (m) — zazwyczaj 2.4–2.8 m
- Policz widoczne otwory: drzwi, okna (każde ok. 1.6–2.0 m²)

KROK 3 — OBLICZ METRAŻ:
Wzór: Powierzchnia_ścian = (Obwód_szacowany × Wysokość) − Σ_otwory
Powierzchnia_podłogi = Szacowana_długość × Szacowana_szerokość

KROK 4 — WYKRYJ ELEMENTY TECHNOLOGICZNE:
- Stan powierzchni: surowy beton / stary tynk / gładź gotowa / stara farba / płytki
- Czy widać wilgoć, zacieki, pęknięcia? (ryzyko dodatkowych prac)
- Szacunkowa liczba gniazdek/włączników widocznych
- Czy to strefa mokra (łazienka, kuchnia)?

KROK 5 — ZWRÓĆ CZYSTY JSON (bez żadnego tekstu przed ani po):
{
  "spatial": {
    "flatSurfaceM2": <liczba m² ścian do gładzi/malowania>,
    "floorAreaM2": <liczba m² podłogi>,
    "wallHeightM": <wysokość w metrach>,
    "perimeterM": <obwód pomieszczenia w mb>,
    "openingsCount": <liczba drzwi + okien>,
    "openingsAreaM2": <suma powierzchni otworów>,
    "edgesMB": <długość narożników ścian w mb — do akrylowania>,
    "confidence": <0-100, Twoja pewność szacunku>,
    "referenceObject": "DOOR_90x200" | "SOCKET_80MM" | "BRICK" | "WINDOW" | "NONE",
    "scaleFactor": <px na cm, jeśli możliwe do oszacowania, else 0>,
    "detectedMaterial": "<pl: surowy beton | stary tynk | gładź | stara farba | płytki | nieznany>"
  },
  "risks": {
    "moistureDetected": <true/false>,
    "cracksDetected": <true/false>,
    "wetZone": <true/false>,
    "oldPaintPeeling": <true/false>
  },
  "scope": "painting" | "bathroom" | "general"
}

WAŻNE: Jeśli nie możesz znaleźć żadnego punktu odniesienia lub obraz jest niskiej jakości,
ustaw "confidence": poniżej 40 i podaj najlepsze możliwe szacunki.
Zawsze zwracaj poprawny JSON — to jest wymaganie techniczne systemu.
`;

export async function analyzeSpatial(imageFile: File): Promise<SpatialData> {
  void imageFile;
  console.warn('[vision] AI chwilowo offline. Możesz policzyć ręcznie.');
  return buildFallback(0);
}

function parseGeminiResponse(text: string): SpatialData {
  try {
    // Wytnij JSON z odpowiedzi (Gemini czasem dodaje tekst wokół)
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('Brak JSON w odpowiedzi');

    const parsed = JSON.parse(jsonMatch[0]);
    const s = parsed.spatial ?? {};
    const r = parsed.risks ?? {};

    const confidence = Number(s.confidence ?? 0);

    return {
      flatSurfaceM2: Number(s.flatSurfaceM2 ?? 0),
      floorAreaM2: Number(s.floorAreaM2 ?? 0),
      wallHeightM: Number(s.wallHeightM ?? 2.6),
      perimeterM: Number(s.perimeterM ?? 0),
      openingsAreaM2: Number(s.openingsAreaM2 ?? 0),
      edgesMB: Number(s.edgesMB ?? 0),
      confidence,
      referenceObject: s.referenceObject ?? 'NONE',
      scaleFactor: Number(s.scaleFactor ?? 0),
      detectedMaterial: s.detectedMaterial ?? 'nieznany',
      // Ryzyka → przekazujemy do reguł
      moistureDetected: Boolean(r.moistureDetected),
      cracksDetected: Boolean(r.cracksDetected),
      wetZone: Boolean(r.wetZone),
      scope: parsed.scope ?? 'painting',
    };
  } catch {
    return buildFallback(30); // niska ufność → REFERENCE_SELECT
  }
}

function buildFallback(confidence: number): SpatialData {
  return {
    flatSurfaceM2: 0,
    floorAreaM2: 0,
    wallHeightM: 2.6,
    perimeterM: 0,
    openingsAreaM2: 0,
    edgesMB: 0,
    confidence,
    referenceObject: 'NONE',
    scaleFactor: 0,
    detectedMaterial: 'nieznany',
    moistureDetected: false,
    cracksDetected: false,
    wetZone: false,
    scope: 'painting',
  };
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Usuń prefix "data:image/...;base64,"
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Pomocnicze: przelicz z reference manualnego

export function calculateFromReference(
  pixelDimensions: { width: number; height: number },
  reference: SpatialData['referenceObject']
): SpatialData {
  const REFS = {
    DOOR_90x200: { widthCm: 90, heightCm: 200 },
    SOCKET_80MM: { widthCm: 8, heightCm: 8 },
    BRICK: { widthCm: 25, heightCm: 6.5 },
    WINDOW: { widthCm: 100, heightCm: 130 },
    NONE: { widthCm: 90, heightCm: 200 },
  };

  const ref = REFS[reference] ?? REFS['DOOR_90x200'];
  const pxPerCm = pixelDimensions.width / ref.widthCm;
  const widthM = pixelDimensions.width / pxPerCm / 100;
  const heightM = pixelDimensions.height / pxPerCm / 100;
  const perimeterM = (widthM + heightM) * 2;

  return {
    flatSurfaceM2: parseFloat((perimeterM * 2.6).toFixed(1)),
    floorAreaM2: parseFloat((widthM * heightM).toFixed(1)),
    wallHeightM: 2.6,
    perimeterM: parseFloat(perimeterM.toFixed(1)),
    openingsAreaM2: 1.8,
    edgesMB: parseFloat((perimeterM).toFixed(1)),
    confidence: 65,
    referenceObject: reference,
    scaleFactor: pxPerCm,
    detectedMaterial: 'nieznany',
    moistureDetected: false,
    cracksDetected: false,
    wetZone: false,
    scope: 'painting',
  };
}

